import React, { useState } from "react"

import { gql } from "urql"

import { generateHslColor } from "src/colorGenerator"
import { studentClient as client } from "src/urql-client"

import { useQuery } from "hooks/urql"

import LoadingIndicator from "components/LoadingIndicator"

import Calendar, { CalendarEvent } from "./Calendar"

type AcrClass = {
  id: string
  name: string
  teacher: {
    fullName: string
  }
  subject: {
    name: string
  }
  grade: {
    name: string
  }
  joinUrl: string | null
  acrClassPeriods: {
    id: string
    startsAt: string
    endsAt: string
  }[]
}

const AcrClassesIndex: React.FC = () => {
  const [dateRange, setDateRange] = useState<{
    startDate: string
    endDate: string
  } | null>(null)

  const [result] = useQuery({
    query: acrClassesQuery,
    client,
    pause: dateRange === null,
    variables: dateRange,
  })

  const acrClasses: AcrClass[] = result.data?.viewer.acrClasses || []
  const coloredAcrClasses = acrClasses.map(acrClass => ({
    ...acrClass,
    color: generateHslColor(acrClass.id),
  }))
  const events: CalendarEvent[] = coloredAcrClasses.flatMap(acrClass =>
    acrClass.acrClassPeriods.map(period => ({
      id: period.id,
      title: acrClass.name,
      start: period.startsAt,
      end: period.endsAt,
      backgroundColor: acrClass.color,
      borderColor: acrClass.color,
    }))
  )

  return (
    <div className="row">
      <h1>Air Classrooms</h1>

      {result.fetching && acrClasses.length === 0 && <LoadingIndicator />}
      {result.error && (
        <p className="text-red-600">Unable to load your Air Classrooms.</p>
      )}

      {acrClasses.length > 0 && (
        <div className="mb-8 overflow-x-auto">
          <table className="table-striped table">
            <thead>
              <tr>
                <th>Class</th>
                <th>Teacher</th>
                <th>Subject</th>
                <th>Grade</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {coloredAcrClasses.map(acrClass => (
                <tr key={acrClass.id}>
                  <td>
                    <span className="flex items-center gap-2">
                      <span
                        aria-hidden="true"
                        className="inline-block h-3 w-3 shrink-0 rounded-full"
                        style={{ backgroundColor: acrClass.color }}
                      />
                      {acrClass.name}
                    </span>
                  </td>
                  <td>{acrClass.teacher.fullName}</td>
                  <td>{acrClass.subject.name}</td>
                  <td>{acrClass.grade.name}</td>
                  <td>
                    {acrClass.joinUrl && (
                      <a
                        className="btn solid small blue"
                        href={acrClass.joinUrl}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        Join
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2>Schedule</h2>
      <Calendar events={events} setDateRange={setDateRange} />
    </div>
  )
}

const acrClassesQuery = gql`
  query StudentAcrClassesQuery($startDate: DateTime!, $endDate: DateTime!) {
    viewer {
      acrClasses {
        id
        name
        joinUrl
        teacher {
          fullName
        }
        subject {
          name
        }
        grade {
          name
        }
        acrClassPeriods(startDate: $startDate, endDate: $endDate) {
          id
          startsAt
          endsAt
        }
      }
    }
  }
`

export default AcrClassesIndex
